import { Inject, Injectable } from '@angular/core';
import type {
  BrandConfig,
  BrandMenuItem,
  DigitalFootprintConfig,
  FeatureFlags,
  MenuConfig,
  ThemeConfig,
  WhatsAppContact,
  CompanyInfo,
} from '../../../brands';
import { Brand, BRAND_CONFIG } from '../../../brands';

/**
 * BrandService provides access to the current brand configuration
 * throughout the application. It is injected with the active brand
 * configuration based on the build target.
 */
@Injectable({ providedIn: 'root' })
export class BrandService {
  constructor(@Inject(BRAND_CONFIG) private config: BrandConfig) {}

  /**
   * Brand identity information
   */
  get brand() {
    return this.config.brand;
  }

  get brandName(): Brand {
    return this.config.brand.name;
  }

  /**
   * Check if the current brand matches
   */
  isBrand(brand: Brand): boolean {
    return this.config.brand.name === brand;
  }

  get brandDisplayName(): string {
    return this.config.brand.displayName;
  }

  get brandTagline(): string | undefined {
    return this.config.brand.tagline;
  }

  /**
   * Company information
   */
  get company() {
    return this.config.company;
  }

  get usaCompany(): CompanyInfo {
    return this.config.company.usa;
  }

  get europeCompany(): CompanyInfo {
    return this.config.company.europe;
  }

  /**
   * URLs
   */
  get urls() {
    return this.config.urls;
  }

  get mainUrl(): string {
    return this.config.urls.main;
  }

  get termsUrl(): string {
    return this.config.urls.terms;
  }

  get privacyUrl(): string {
    return this.config.urls.privacy;
  }

  get privacyCenterUrl(): string {
    return this.config.urls.privacyCenter;
  }

  get helpUrl(): string {
    return this.config.urls.help;
  }

  get contactUrl(): string {
    return this.config.urls.contact;
  }

  /**
   * Emails
   */
  get emails() {
    return this.config.emails;
  }

  get salesEmail(): string {
    return this.config.emails.sales;
  }

  get supportEmail(): string {
    return this.config.emails.support;
  }

  get databrokersEmail(): string | undefined {
    return this.config.emails.databrokers;
  }

  /**
   * WhatsApp contacts
   */
  get whatsappContacts(): WhatsAppContact[] {
    return this.config.whatsapp;
  }

  /**
   * Chat configuration
   */
  get chat() {
    return this.config.chat;
  }

  get isChatEnabled(): boolean {
    return this.config.chat.enabled;
  }

  /**
   * Feature flags
   */
  get features(): FeatureFlags {
    return this.config.features;
  }

  /**
   * Check if a specific feature is enabled
   */
  isFeatureEnabled(feature: keyof FeatureFlags): boolean {
    return this.config.features[feature] ?? false;
  }

  /**
   * Menu configuration
   */
  get menu(): MenuConfig {
    return this.config.menu;
  }

  /**
   * Get filtered menu items based on visibility and feature flags
   */
  getMenuItems(): BrandMenuItem[] {
    return this.filterMenuItems(this.config.menu.items);
  }

  /**
   * Recursively filter menu items based on visibility and feature flags
   */
  private filterMenuItems(items: BrandMenuItem[]): BrandMenuItem[] {
    return items
      .filter(item => {
        if (!item.visible) return false;
        if (item.featureFlag && !this.isFeatureEnabled(item.featureFlag)) {
          return false;
        }
        return true;
      })
      .map(item => {
        if (item.children && item.children.length > 0) {
          const filteredChildren = this.filterMenuItems(item.children);
          // Only include parent if it has visible children
          if (filteredChildren.length === 0) return null;
          return { ...item, children: filteredChildren };
        }
        return item;
      })
      .filter((item): item is BrandMenuItem => item !== null);
  }

  /**
   * Digital footprint configuration
   */
  get digitalFootprint(): DigitalFootprintConfig {
    return this.config.digitalFootprint;
  }

  /**
   * Theme configuration
   */
  get theme(): ThemeConfig {
    return this.config.theme;
  }

  get logoPath(): string {
    return this.config.theme.logo;
  }

  get logoIconPath(): string {
    return this.config.theme.logoIcon;
  }

  get faviconPath(): string {
    return this.config.theme.favicon;
  }

  get primaryColor(): string {
    return this.config.theme.primaryColor;
  }

  /**
   * Get the full brand configuration
   */
  get fullConfig(): BrandConfig {
    return this.config;
  }

  /**
   * Get company names as a formatted string for legal documents
   */
  getCompanyNamesForLegal(): string {
    return `${this.config.company.europe.name} and ${this.config.company.usa.name} (${this.brandDisplayName})`;
  }
}
